import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { SortState } from "@/components/organisms/history/types";
import { ExtendedPagedResult } from "@/repository/pagedResult";
import {
	Query,
	useInfiniteQuery,
	useQueryClient,
	InfiniteData,
	UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { InViewHookResponse, useInView } from "react-intersection-observer";

export type QueryResult<T> = UseInfiniteQueryResult<
	InfiniteData<ExtendedPagedResult<T>, unknown>,
	Error
>;

export class TotalCount {
	filtered: number = 0;
	raw: number = 0;
}

export interface InfiniteQueryFnParams {
	page: number;
	searchValue?: string;
	sorting?: SortState;
	sessionId?: string;
	filter?: any;
}

type QueryFunction<T> = (
	params: InfiniteQueryFnParams
) => Promise<ExtendedPagedResult<T>>;

export const useInfiniteQueryHook = <T extends { id: string }>(
	key: string,
	queryFn: QueryFunction<T>,
	cacheKey: CacheKey,
	enabled: boolean,
	sortCacheKey?: CacheKey,
	sessionId?: string,
	refetchOnMount?: boolean | "always",
	allowUndefinedFilter: boolean = true
): [
	result: QueryResult<T>,
	totalCount: TotalCount,
	inView: InViewHookResponse,
	setSearchValue: (value: string) => void,
	setSorting: (value: SortState) => void,
	setFilter: (value: any) => void,
	updateInfiniteItem: (targetId: string, newItem: T) => void,
] => {
	const appStore = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreSetter = useAppCacheStore(
		(state: AppCacheActions) => state.setCache
	);

	const [searchValue, setSearchValue] = useState<string>(
		appStore[cacheKey] ?? ""
	);

	const [sorting, setSorting] = useState<SortState>(
		sortCacheKey && appStore[sortCacheKey]
			? appStore[sortCacheKey]
			: ({} as SortState)
	);

	const [filter, setFilter] = useState<any>();

	const inView = useInView();
	const queryClient = useQueryClient();
	const result = useInfiniteQuery({
		enabled: enabled && (allowUndefinedFilter || !!filter),
		initialPageParam: 0,
		queryKey: [key, searchValue, sorting, filter, sessionId],
		queryFn: (context: any) =>
			queryFn({
				page: context.pageParam ?? 0,
				searchValue,
				sorting,
				sessionId,
				filter,
			}),
		getNextPageParam: (
			page: ExtendedPagedResult<T>,
			all: ExtendedPagedResult<T>[]
		) =>
			all.flatMap((x) => x.items).length < page.totalCount
				? all.length
				: undefined,
		refetchOnMount: refetchOnMount,
	});

	const { fetchNextPage, hasNextPage, isFetchingNextPage, isError } = result;

	const loadedItemCount = useMemo(
		() =>
			result.data?.pages.reduce(
				(total: number, page: ExtendedPagedResult<T>) => total + page.items.length,
				0
			) ?? 0,
		[result.data]
	);

	// Item count at the last auto-fetch, so a fetch that returned nothing new is not repeated.
	const lastAutoFetchCount = useRef<number | null>(null);

	// Re-runs whenever a page finishes loading, not only on the sentinel's visibility transition: if the
	// loaded page does not push the sentinel out of view, inView stays true (no change event) and
	// pagination would otherwise stall on the spinner. fetchNextPage is stable across renders.
	// Guards:
	// - isError: a persistently failing next-page request would otherwise retry-loop, because React Query
	//   exhausts its retries, isFetchingNextPage flips back to false and the sentinel is still in view.
	// - lastAutoFetchCount: a page that adds no items leaves hasNextPage true when the backend reports a
	//   totalCount it cannot serve, which would otherwise fetch page after empty page without end. The
	//   count resets when the sentinel leaves the viewport, so scrolling back retries.
	useEffect(() => {
		if (!inView.inView) {
			lastAutoFetchCount.current = null;
		} else if (
			hasNextPage &&
			!isFetchingNextPage &&
			!isError &&
			lastAutoFetchCount.current !== loadedItemCount
		) {
			lastAutoFetchCount.current = loadedItemCount;
			void fetchNextPage();
		}
	}, [
		inView.inView,
		hasNextPage,
		isFetchingNextPage,
		isError,
		loadedItemCount,
		fetchNextPage,
	]);

	const totalCounts = useMemo<TotalCount>(() => {
		const pages = result.data?.pages;

		if (!pages?.length) {
			return new TotalCount();
		}

		const lastPage = pages[pages.length - 1];

		return {
			filtered: lastPage?.totalCount || 0,
			raw: lastPage?.rawTotalCount || 0,
		};
	}, [result]);

	useEffect(() => {
		if (result.isFetched && !!result.data) {
			queryClient.removeQueries({
				predicate: (query: Query) =>
					query.queryKey[0] === key && !query.getObserversCount(),
			});
		}
	}, [result.isFetched]);

	useEffect(() => {
		appStoreSetter(cacheKey, searchValue);
	}, [searchValue]);

	useEffect(() => {
		if (sortCacheKey) {
			appStoreSetter(sortCacheKey, sorting);
		}
	}, [sorting]);

	const updateInfiniteItem = (targetId: string, newItem: T) => {
		queryClient.setQueryData<InfiniteData<ExtendedPagedResult<T>>>(
			[key, searchValue, sorting, filter, sessionId],
			(old) => {
				if (!old) {
					return old;
				}

				return {
					...old,
					pages: old.pages.map((page) => ({
						...page,
						items: page.items.map((item) =>
							item.id === targetId ? newItem : item
						),
					})),
				};
			}
		);
	};

	return [
		result,
		totalCounts,
		inView,
		setSearchValue,
		setSorting,
		setFilter,
		updateInfiniteItem,
	];
};
